import {Disposable, Environment} from 'relay-runtime';
import {commitMutation, graphql} from 'react-relay';
import Util from '../../forecast-app/shared/util/util';
import {DismissNovaRunInput, DismissNovaRunMutation} from './__generated__/DismissNovaRunMutation.graphql';
import {MutationInput, RelayOnCompleted, RelayOnError} from '../MutationTypes';

// Clears one failed run from the thread it sits on. The run record stays: dismissing is the
// customer clearing a notice that has no work in it, not a request to erase what was attempted or
// why it failed.
//
// Not a narrower discard. Discarding retires the whole conversation the run belongs to, which is
// right for a draft nobody wants and wrong for an error message sitting under answers somebody does
// — so this is its own mutation against its own column, and the turns above the dismissed one are
// untouched by it.
//
// Only `dismissed` is really read. The thread the task modal renders comes from task.novaThread,
// which re-reads the surviving conversation for itself, so there is nothing on this payload a
// caller needs beyond confirmation that the dismiss landed. `id` is selected because Relay needs it
// to place the record in its store.
const mutation = graphql`
	mutation DismissNovaRunMutation($input: DismissNovaRunInput!) {
		dismissNovaRun(input: $input) {
			run {
				id
				dismissed
			}
			# Selected for the same reason the discard mutation selects it: the task modal loads through
			# ForecastQueryRenderer, whose store-and-network policy paints the cached store before the
			# network answers, so without replacing this list here a reopened task renders the dismissed
			# error for a frame and then drops it when the fetch comes back. Writing the surviving thread
			# straight into the store means there is never a stale version to paint.
			#
			# hasNovaConversation comes back for the case where the dismissed run was the only thing the
			# thread had: the task then has no conversation, and every row showing it must return to
			# offering a fresh run rather than to offering Continue on nothing.
			task {
				id
				hasNovaConversation
				# Selected whole rather than by id, unlike the discard mutation beside it. A discard
				# leaves the list empty, so there are no fields to be missing; a dismiss usually leaves
				# turns standing, and a record written with only an id would blank the fields the thread
				# renders off any surviving run the store did not already hold in full - a run that
				# arrived through the run mutation's own result.run, say, which carries no grounding.
				# Same shape the run mutation and the task modal's own query select.
				novaThread {
					id
					runId
					threadId
					agentId
					versionNumber
					status
					outcome
					answer
					userMessageText
					actingPerson {
						fullName
						profilePictureId
					}
					ranAt
					failureState
					grounding {
						resolved {
							token
							scope
						}
						skipped {
							token
						}
					}
				}
			}
		}
	}
`;

function commit(
	environment: Environment,
	input: MutationInput<DismissNovaRunInput>,
	onSuccess?: RelayOnCompleted<DismissNovaRunMutation>,
	onError?: RelayOnError
): Disposable {
	const variables = {input: {csrfToken: Util.getCsrfValue(), socketClientId: Util.getClientId(), ...input}};
	return commitMutation<DismissNovaRunMutation>(environment, {
		mutation,
		variables,
		onCompleted: onSuccess,
		onError: onError,
	});
}

export default {commit};
